import React from 'react';
import { Box, IconButton, Avatar, Tooltip, useTheme } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BuildIcon from '@mui/icons-material/Build';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import Inventory2Icon from '@mui/icons-material/Inventory2';

interface SidebarProps {
  activeIndex?: number;
  onSelect?: (index: number) => void;
  onSettingsClick?: () => void;
}

const icons = [
  { icon: <DashboardIcon />, label: 'Dashboard' },
  { icon: <Inventory2Icon />, label: 'Assets' },
  { icon: <BuildIcon />, label: 'Maintenance' },
  { icon: <ScienceIcon />, label: 'Modeling' },
];

export default function Sidebar({ activeIndex = 0, onSelect, onSettingsClick }: SidebarProps) {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        width: 72,
        height: '100vh',
        bgcolor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxShadow: '0 4px 24px 0 rgba(124,58,237,0.08)',
        py: 2,
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        borderRadius: 0,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ mb: 2, mt: 1 }}>
        <img 
          src="/viaconnect.png" 
          alt="Via Logo" 
          style={{ 
            width: '48px', 
            height: '48px',
            borderRadius: '6px'
          }} 
        />
      </Box>

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {icons.map((item, idx) => (
          <Tooltip 
            title={item.label} 
            placement="right" 
            key={item.label}
            arrow={false}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'rgba(0, 0, 0, 0.87)',
                  color: 'white',
                  fontSize: '0.75rem',
                  '& .MuiTooltip-arrow': {
                    display: 'none'
                  }
                }
              }
            }}
          >
            <IconButton
              onClick={() => onSelect && onSelect(idx)}
              sx={{
                color: idx === activeIndex ? theme.palette.primary.main : theme.palette.text.secondary,
                bgcolor: idx === activeIndex ? theme.palette.action.selected : 'transparent',
                borderRadius: 2,
                mb: 1,
                width: 48,
                height: 48,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                },
                transition: 'background 0.2s',
              }}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ position: 'absolute', left: 0, bottom: 16, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <IconButton 
          color="primary" 
          onClick={onSettingsClick} 
          sx={{ bgcolor: theme.palette.action.hover, mb: 1 }}
        >
          <SettingsIcon />
        </IconButton>
        <Avatar 
          sx={{ 
            width: 40, 
            height: 40, 
            bgcolor: theme.palette.primary.main, 
            color: 'white', 
            fontWeight: 700, 
            fontSize: 20, 
            boxShadow: theme.palette.mode === 'dark' ? '0 2px 8px 0 rgba(124,58,237,0.30)' : '0 2px 8px 0 rgba(124,58,237,0.10)' 
          }}
        >
          KK
        </Avatar>
      </Box>
    </Box>
  );
} 